import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

// Command Deck panel: pending DSS AI registrations with admin approve/reject.
// On approval the backend creates the client login — the temp password is shown
// here ONCE for the admin to copy and share with the customer.
export default function RegistrationsPanel({ isAdmin }) {
  const [regs, setRegs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creds, setCreds] = useState(null); // {email, temp_password}
  const [busy, setBusy] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/registrations");
      setRegs(data);
    } catch {
      setRegs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const decide = async (reg, decision) => {
    setBusy(reg.id);
    try {
      const { data } = await api.post(`/registrations/${reg.id}/decide`, { decision });
      if (decision === "approved") {
        if (data.temp_password) {
          setCreds({ email: data.email, temp_password: data.temp_password, name: reg.name });
          toast.success(`Approved — login created for ${data.email}`);
        } else {
          toast.success(`Approved — ${data.email} already had an account`);
        }
      } else {
        toast.success("Registration rejected");
      }
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  const copyCreds = async () => {
    if (!creds) return;
    const text = `DSS AI login\nEmail: ${creds.email}\nPassword: ${creds.temp_password}`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied — share it with the customer");
    } catch {
      toast.error("Copy failed — note it down manually");
    }
  };

  const pending = regs.filter((r) => r.status === "pending");
  const decided = regs.filter((r) => r.status !== "pending");

  return (
    <div className="accent-bar premium-card rounded-xl bg-white dark:bg-slate-900/80 border border-slate-200 dark:border-white/[0.08] overflow-hidden" data-testid="registrations-card">
      <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-white/[0.06]">
        <div className="text-xs uppercase code-font neon-text font-bold tracking-wider">DSS AI Registrations</div>
        <span className="text-[11px] code-font text-slate-500 dark:text-slate-400" data-testid="registrations-count">
          {pending.length} pending · {regs.length} total
        </span>
      </div>

      {creds && (
        <div className="m-4 rounded-xl border border-emerald-300 dark:border-emerald-600/40 bg-emerald-50 dark:bg-emerald-500/10 p-4" data-testid="registration-creds">
          <div className="text-xs font-bold text-emerald-800 dark:text-emerald-300 uppercase code-font tracking-wider">Login created for {creds.name}</div>
          <div className="mt-2 text-sm text-slate-800 dark:text-slate-200 code-font">
            Email: {creds.email}<br />Password: {creds.temp_password}
          </div>
          <div className="mt-3 flex gap-2">
            <button onClick={copyCreds} className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-[11px] font-bold uppercase tracking-wider hover:bg-emerald-700 transition-colors" data-testid="copy-creds-button">
              Copy login details
            </button>
            <button onClick={() => setCreds(null)} className="px-3 py-1.5 rounded-lg border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 text-[11px] font-bold uppercase tracking-wider" data-testid="dismiss-creds-button">
              Done
            </button>
          </div>
          <p className="mt-2 text-[11px] text-emerald-700 dark:text-emerald-400">Shown once — share it with the customer now. They can sign in at the Work Desk login.</p>
        </div>
      )}

      {loading ? (
        <div className="p-6 text-sm text-slate-500 dark:text-slate-400">Loading…</div>
      ) : regs.length === 0 ? (
        <div className="p-6 text-sm text-slate-500 dark:text-slate-400" data-testid="registrations-empty">No registrations yet — they appear here when someone registers from the landing page.</div>
      ) : (
        <div className="divide-y divide-slate-100 dark:divide-white/[0.04]">
          {[...pending, ...decided].map((r) => (
            <div key={r.id} className="flex flex-wrap items-center gap-3 px-4 py-3" data-testid={`registration-row-${r.id}`}>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-slate-900 dark:text-white truncate">
                  {r.name} <span className="text-xs font-normal text-slate-500 dark:text-slate-400">· {r.plan_name} ({r.plan_price})</span>
                </div>
                <div className="text-xs text-slate-500 dark:text-slate-400 code-font truncate">{r.email} · {r.mobile}</div>
                {r.message && <div className="text-xs text-slate-500 dark:text-slate-400 italic truncate">“{r.message}”</div>}
              </div>
              {r.status === "pending" ? (
                isAdmin ? (
                  <div className="flex gap-2">
                    <button
                      onClick={() => decide(r, "approved")}
                      disabled={busy === r.id}
                      className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-[10px] font-bold uppercase tracking-wider hover:bg-emerald-700 transition-colors disabled:opacity-60"
                      data-testid={`registration-approve-${r.id}`}
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => decide(r, "rejected")}
                      disabled={busy === r.id}
                      className="px-3 py-1.5 rounded-lg border border-rose-300 dark:border-rose-700/50 text-rose-600 dark:text-rose-300 text-[10px] font-bold uppercase tracking-wider hover:bg-rose-50 dark:hover:bg-rose-500/10 transition-colors disabled:opacity-60"
                      data-testid={`registration-reject-${r.id}`}
                    >
                      Reject
                    </button>
                  </div>
                ) : (
                  <span className="text-[10px] code-font uppercase text-amber-600 dark:text-amber-300 border border-amber-200 dark:border-amber-700/40 bg-amber-50 dark:bg-amber-500/10 px-2 py-1 rounded-full">Awaiting admin</span>
                )
              ) : (
                <span className={`text-[10px] code-font uppercase px-2 py-1 rounded-full border ${
                  r.status === "approved"
                    ? "text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-700/40 bg-emerald-50 dark:bg-emerald-500/10"
                    : "text-rose-600 dark:text-rose-300 border-rose-200 dark:border-rose-700/40 bg-rose-50 dark:bg-rose-500/10"
                }`} data-testid={`registration-status-${r.id}`}>
                  {r.status}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
